/**
 * The shared status glyphs, named for what they mean rather than for any one
 * lifecycle. A feature maps its own statuses onto these; colours follow the
 * StatusBadge tones, so an icon never disagrees with the badge it stands in for.
 */
export type StatusIconKind =
  /** Not yet real. */
  | "draft"
  /** Ready to start. */
  | "todo"
  /** Nothing happened yet. */
  | "idle"
  /** Under way. */
  | "active"
  /** Done, being corrected. */
  | "revising"
  /** Waiting, will resume. */
  | "paused"
  /** Finished as intended. */
  | "done"
  /** Finished, corrected since. */
  | "revised"
  /** Deliberately not done. */
  | "skipped"
  /** Got partway, could not finish. */
  | "partial"
  /** Halted partway on purpose. */
  | "stopped"
  /** Called off. */
  | "cancelled"
  /** Removed from the plan. */
  | "withdrawn"
  /** Failed, or the record is wrong. */
  | "error";

function Svg({ size, className, children }: { size: number; className: string; children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" className={className} aria-hidden="true">
      {children}
    </svg>
  );
}

export function StatusIcon({
  status,
  size = 14,
  className,
}: {
  status: StatusIconKind | undefined;
  size?: number;
  className?: string;
}) {
  const cls = className ?? "shrink-0";

  switch (status) {
    case "draft":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
            className="text-muted-foreground/60"
          />
        </Svg>
      );
    case "todo":
      return (
        <Svg size={size} className={cls}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
        </Svg>
      );
    case "active":
      return (
        <Svg size={size} className={cls}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-500" />
          <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7 L7 7 Z" fill="currentColor" className="text-blue-500" />
        </Svg>
      );
    case "paused":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-amber-500"
          />
          <rect x="5" y="4.5" width="1.5" height="5" rx="0.5" fill="currentColor" className="text-amber-500" />
          <rect x="7.5" y="4.5" width="1.5" height="5" rx="0.5" fill="currentColor" className="text-amber-500" />
        </Svg>
      );
    case "done":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-emerald-500"
          />
          <path
            d="M4.5 7 L6.2 8.7 L9.5 5.3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-emerald-500"
          />
        </Svg>
      );
    case "cancelled":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-muted-foreground/50"
          />
          <path
            d="M5 5 L9 9 M9 5 L5 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="text-muted-foreground/50"
          />
        </Svg>
      );
    case "error":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-destructive"
          />
          <path d="M7 4.5 L7 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="7" cy="9.5" r="0.75" fill="white" />
        </Svg>
      );
    case "idle":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-muted-foreground"
          />
        </Svg>
      );
    case "skipped":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-amber-500"
          />
          <path d="M3.1 10.9 L10.9 3.1" stroke="currentColor" strokeWidth="1.5" className="text-amber-500" />
        </Svg>
      );
    case "partial":
      // Half filled: got partway, where active's quarter is still going.
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-amber-500"
          />
          <path d="M7 1.5 A5.5 5.5 0 0 0 7 12.5 Z" fill="currentColor" className="text-amber-500" />
        </Svg>
      );
    case "stopped":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-amber-500"
          />
          <rect x="4.75" y="4.75" width="4.5" height="4.5" rx="0.75" fill="currentColor" className="text-amber-500" />
        </Svg>
      );
    case "withdrawn":
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
            className="text-muted-foreground/50"
          />
          <path
            d="M4.5 7 L9.5 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="text-muted-foreground/50"
          />
        </Svg>
      );
    case "revised":
      // Solid where done is outlined: finished, then corrected and settled again.
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-emerald-500"
          />
          <path
            d="M4.5 7 L6.2 8.7 L9.5 5.3"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "revising":
      return (
        <Svg size={size} className={cls}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-500" />
          <path d="M4.6 9.4 L5 7.8 L8.2 4.6 L9.4 5.8 L6.2 9 Z" fill="currentColor" className="text-blue-500" />
        </Svg>
      );
    default:
      return (
        <Svg size={size} className={cls}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-muted-foreground/40"
          />
        </Svg>
      );
  }
}
